import { useState } from 'react'
import { Skeleton } from 'antd'
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates'
import { SegmentControl } from '@forwardflow/ui-v2'
import { useGetUsageSummary } from '@forwardflow/hooks/customHooks/useGetUsageSummary'
import { useGetMonthlyBudgets } from '@forwardflow/hooks/customHooks/useGetMonthlyBudgets'
import dayjs from 'dayjs'

type TPeriod = 'this-month' | 'last-month' | 'last-7' | 'last-30' | 'custom'

const PERIOD_OPTIONS = [
   { label: 'This Month', value: 'this-month' },
   { label: 'Last Month', value: 'last-month' },
   { label: 'Last 7 Days', value: 'last-7' },
   { label: 'Last 30 Days', value: 'last-30' },
   { label: 'Custom', value: 'custom' },
]

const formatCost = (cost: number | null | undefined) => `$${(Number(cost) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

interface UsageRowProps {
   color: string
   name: string
   subtitle: string
   count: string
   cost: number
   percentage: number
}

const UsageRow = ({ color, name, subtitle, count, cost, percentage }: UsageRowProps) => (
   <div className="flex items-center gap-4 py-3.5 px-5 border-b border-white/[0.06] last:border-b-0 hover:bg-white/[0.015] transition-colors">
      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
      <div className="flex-1 min-w-0">
         <p className="text-white/70 text-sm">{name}</p>
         <p className="text-white/30 text-xs mt-0.5">{subtitle}</p>
      </div>
      <div className="w-28 shrink-0">
         <div className="w-full h-1 bg-white/[0.06] rounded-full">
            <div
               className="h-full rounded-full transition-all duration-500"
               style={{ width: `${Math.min(percentage, 100)}%`, background: color }}
            />
         </div>
      </div>
      <span className="text-white/30 text-xs min-w-[70px] text-right shrink-0">{count}</span>
      <span className="text-white font-semibold text-sm min-w-[60px] text-right shrink-0">{formatCost(cost)}</span>
   </div>
)

export const UsageTab = () => {
   const [period, setPeriod] = useState<TPeriod>('this-month')
   const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null])

   const startDate = period === 'custom' && dateRange[0]
      ? dayjs(dateRange[0]).format('YYYY-MM-DD')
      : undefined
   const endDate = period === 'custom' && dateRange[1]
      ? dayjs(dateRange[1]).format('YYYY-MM-DD')
      : undefined

   const shouldFetch = period !== 'custom' || (!!startDate && !!endDate)
   const { data, isLoading, error } = useGetUsageSummary(
      shouldFetch ? period : null,
      startDate,
      endDate,
   )
   const { data: budgetData } = useGetMonthlyBudgets()

   const handlePeriodChange = (value: string) => {
      setPeriod(value as TPeriod)
      if (value !== 'custom') {
         setDateRange([null, null])
      }
   }

   const calls = data?.calls ?? { ringingOnlyCost: 0, ringingOnlyCount: 0, callTimeCost: 0, callTimeCount: 0, callTimeRingingCost: 0, callTimeTalkCost: 0 }
   const sms = data?.sms ?? { cost: 0, count: 0 }
   const tokens = data?.tokens ?? { cost: 0, count: 0 }
   const totalSpent = data?.totalSpent ?? 0

   const totalCallsCost = calls.callTimeCost + calls.ringingOnlyCost
   const totalCallsCount = calls.callTimeCount + calls.ringingOnlyCount

   const effectiveRingingCost = calls.callTimeRingingCost
   const effectiveTalkCost = calls.callTimeCost - calls.callTimeRingingCost

   const maxCost = Math.max(totalCallsCost, sms.cost, tokens.cost, 1)

   return (
      <>
         {/* Campaign Spend — always current month, outside filtered area */}
         {budgetData && (
            <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
               <div className="flex items-center justify-between py-4 px-5 bg-white/[0.02]">
                  <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">Spent This Month</span>
                  <span className="text-white font-semibold text-sm">
                     {formatCost((budgetData.currentMonthExpenses?.newLead ?? 0) + (budgetData.currentMonthExpenses?.revive ?? 0))}
                     <span className="text-white/30 font-normal"> of {formatCost((budgetData.newLeadBudget ?? 0) + (budgetData.reactivateLeadBudget ?? 0))}</span>
                  </span>
               </div>
               <div>
                  <div className="flex items-center gap-4 py-3.5 px-5 border-b border-white/[0.06]">
                     <div className="w-2 h-2 rounded-full shrink-0 bg-[#8b5cf6]" />
                     <p className="text-white/70 text-sm flex-1">New Leads Nurture</p>
                     <span className="text-white/30 text-xs">
                        Limit: ${budgetData.newLeadBudget}
                     </span>
                     <span className="text-white font-semibold text-sm min-w-[60px] text-right">
                        {formatCost(budgetData.currentMonthExpenses?.newLead ?? 0)}
                     </span>
                  </div>
                  <div className="flex items-center gap-4 py-3.5 px-5">
                     <div className="w-2 h-2 rounded-full shrink-0 bg-[#f472b6]" />
                     <p className="text-white/70 text-sm flex-1">Cold Leads Revival</p>
                     <span className="text-white/30 text-xs">
                        Limit: ${budgetData.reactivateLeadBudget}
                     </span>
                     <span className="text-white font-semibold text-sm min-w-[60px] text-right">
                        {formatCost(budgetData.currentMonthExpenses?.revive ?? 0)}
                     </span>
                  </div>
               </div>
            </div>
         )}

         {/* Period Filter */}
         <div className="flex items-center gap-4 flex-wrap">
            <SegmentControl
               value={period}
               onChange={handlePeriodChange}
               data={PERIOD_OPTIONS}
            />
            {period === 'custom' && (
               <DatePickerInput
                  type="range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={setDateRange}
                  maxDate={new Date()}
                  numberOfColumns={2}
                  popoverProps={{ withinPortal: true }}
                  styles={{
                     input: {
                        background: 'rgba(71, 71, 71, 0.1)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff',
                        borderRadius: '10px',
                        height: '38px',
                        minWidth: '260px',
                        fontSize: '13px',
                     },
                     calendarHeader: { color: '#fff' },
                  }}
               />
            )}
         </div>

         {isLoading ? (
            <div className="flex flex-col gap-6">
               <Skeleton active />
               <Skeleton active />
               <Skeleton active />
            </div>
         ) : error ? (
            <div className="bg-[#474747]/10 rounded-xl border border-white/10 p-8">
               <p className="text-red-400 text-sm">Failed to load usage data</p>
            </div>
         ) : period === 'custom' && !startDate ? (
            <div className="flex items-center justify-center py-20">
               <p className="text-white/40 text-sm">Select a date range to view usage</p>
            </div>
         ) : (
            <>
               {/* Top Stats Grid */}
               <div className="grid grid-cols-4 gap-4">
                  <StatCard icon="phone" label="AI Calls" value={totalCallsCost} />
                  <StatCard icon="message" label="Messages" value={sms.cost} />
                  <StatCard icon="brain" label="AI Tokens" value={tokens.cost} />
                  <StatCard icon="total" label="Total Spent" value={totalSpent} highlighted />
               </div>

               {/* AI Calls Breakdown */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-4 px-5 bg-white/[0.02]">
                     <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">AI Calls</span>
                     <span className="text-white font-semibold text-sm">{formatCost(totalCallsCost)}</span>
                  </div>
                  <div>
                     {calls.callTimeCount > 0 && (
                        <UsageRow
                           color="#8b5cf6"
                           name="Answered Calls"
                           subtitle={`${calls.callTimeCount} calls`}
                           count={`${calls.callTimeCount} calls`}
                           cost={calls.callTimeCost}
                           percentage={(calls.callTimeCost / maxCost) * 100}
                        />
                     )}
                     {calls.callTimeCount > 0 && (
                        <div className="ml-6">
                           {effectiveRingingCost > 0 && (
                              <UsageRow
                                 color="#a78bfa"
                                 name="Ringing Time"
                                 subtitle="Charged per minute (ceiled)"
                                 count=""
                                 cost={effectiveRingingCost}
                                 percentage={(effectiveRingingCost / maxCost) * 100}
                              />
                           )}
                           <UsageRow
                              color="#c4b5fd"
                              name="Talk Time"
                              subtitle="Includes talk duration"
                              count=""
                              cost={effectiveTalkCost}
                              percentage={(effectiveTalkCost / maxCost) * 100}
                           />
                        </div>
                     )}
                     {calls.ringingOnlyCount > 0 && (
                        <UsageRow
                           color="#7c3aed"
                           name="Unanswered Calls (Ringing Only)"
                           subtitle={`${calls.ringingOnlyCount} calls`}
                           count={`${calls.ringingOnlyCount} calls`}
                           cost={calls.ringingOnlyCost}
                           percentage={(calls.ringingOnlyCost / maxCost) * 100}
                        />
                     )}
                     {totalCallsCount === 0 && (
                        <div className="py-8 text-center text-white/30 text-sm">No call usage this period</div>
                     )}
                  </div>
                  {totalCallsCount > 0 && (
                     <div className="flex items-center justify-between py-3 px-5 bg-white/[0.02] border-t border-white/[0.06]">
                        <span className="text-white/40 text-xs">Total calls spend</span>
                        <span className="text-white font-semibold text-sm">{formatCost(totalCallsCost)}</span>
                     </div>
                  )}
               </div>

               {/* Messages Breakdown */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-4 px-5 bg-white/[0.02]">
                     <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">Messages</span>
                     <span className="text-white font-semibold text-sm">{formatCost(sms.cost)}</span>
                  </div>
                  <div>
                     {sms.count > 0 ? (
                        <UsageRow
                           color="#60a5fa"
                           name="SMS"
                           subtitle={`${sms.count.toLocaleString()} messages sent`}
                           count={`${sms.count.toLocaleString()} SMS`}
                           cost={sms.cost}
                           percentage={(sms.cost / maxCost) * 100}
                        />
                     ) : (
                        <div className="py-8 text-center text-white/30 text-sm">No SMS usage this period</div>
                     )}
                  </div>
                  {sms.count > 0 && (
                     <div className="flex items-center justify-between py-3 px-5 bg-white/[0.02] border-t border-white/[0.06]">
                        <span className="text-white/40 text-xs">Total messages spend</span>
                        <span className="text-white font-semibold text-sm">{formatCost(sms.cost)}</span>
                     </div>
                  )}
               </div>

               {/* LLM Tokens Breakdown */}
               <div className="bg-[#474747]/10 rounded-xl border border-white/10 overflow-hidden">
                  <div className="flex items-center justify-between py-4 px-5 bg-white/[0.02]">
                     <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">AI Tokens</span>
                     <span className="text-white font-semibold text-sm">{formatCost(tokens.cost)}</span>
                  </div>
                  <div>
                     {tokens.count > 0 ? (
                        <UsageRow
                           color="#34d399"
                           name="LLM Processing"
                           subtitle={`${tokens.count.toLocaleString()} token operations`}
                           count={`${tokens.count.toLocaleString()}`}
                           cost={tokens.cost}
                           percentage={(tokens.cost / maxCost) * 100}
                        />
                     ) : (
                        <div className="py-8 text-center text-white/30 text-sm">No token usage this period</div>
                     )}
                  </div>
                  {tokens.count > 0 && (
                     <div className="flex items-center justify-between py-3 px-5 bg-white/[0.02] border-t border-white/[0.06]">
                        <span className="text-white/40 text-xs">Total AI spend</span>
                        <span className="text-white font-semibold text-sm">{formatCost(tokens.cost)}</span>
                     </div>
                  )}
               </div>

            </>
         )}
      </>
   )
}

const StatCard = ({
   icon,
   label,
   value,
   highlighted = false,
}: {
   icon: string
   label: string
   value: number
   highlighted?: boolean
}) => {
   const iconMap: Record<string, string> = {
      phone: '\u{1F4DE}',
      message: '\u{1F4AC}',
      brain: '\u{1F9E0}',
      total: '\u{26A1}',
   }

   return (
      <div
         className={`bg-[#474747]/10 rounded-xl border overflow-hidden p-5 ${
            highlighted ? 'border-primary/30 bg-primary/[0.04]' : 'border-white/10'
         }`}
      >
         <span className="text-lg">{iconMap[icon]}</span>
         <p className="text-white/50 text-xs font-medium mt-2">{label}</p>
         <p className={`text-2xl font-semibold mt-1 ${highlighted ? 'text-primary' : 'text-white'}`}>
            <span className="text-base align-top">$</span>
            {value.toFixed(2)}
         </p>
      </div>
   )
}
